import * as React from 'react';
import { NavLink, useLocation } from 'react-router';
import { PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppRoutes } from '@/hooks/use-app-routes';
import { createNavigation, type NavItem, type NavSection } from '@/lib/navigation';
import { cn } from '@/lib/utils';

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const link = (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      aria-label={collapsed ? item.title : undefined}
      className={({ isActive }) =>
        cn(
          'focus-visible:ring-ring flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
          isActive
            ? 'bg-accent text-accent-foreground font-medium'
            : 'text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground',
          collapsed && 'justify-center'
        )
      }
    >
      <item.icon aria-hidden className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.title}</span>}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.title}</TooltipContent>
    </Tooltip>
  );
}

function SidebarSection({
  section,
  collapsed
}: {
  section: NavSection;
  collapsed: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {!collapsed && (
        <p className="text-muted-foreground px-2 py-1 text-xs font-medium tracking-wide uppercase">
          {section.heading}
        </p>
      )}
      {section.items.map((item) => (
        <SidebarLink key={item.to} item={item} collapsed={collapsed} />
      ))}
    </div>
  );
}

function SidebarPanel({
  collapsed,
  onToggle,
  onClose
}: {
  collapsed: boolean;
  onToggle: () => void;
  onClose?: () => void;
}) {
  const routes = useAppRoutes();
  const navigation = createNavigation(routes);

  return (
    <>
      <div className={cn('flex items-center px-2 pt-2', collapsed ? 'justify-center' : 'justify-end')}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose ?? onToggle}
              aria-label={onClose ? 'Close navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="shrink-0"
            >
              {onClose || !collapsed ? (
                <PanelLeftCloseIcon className="size-4" />
              ) : (
                <PanelLeftOpenIcon className="size-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {onClose ? 'Close' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          </TooltipContent>
        </Tooltip>
      </div>
      <nav aria-label="Main" className="flex flex-1 flex-col gap-3 overflow-y-auto p-2">
        {navigation.map((section) => (
          <SidebarSection key={section.heading} section={section} collapsed={collapsed} />
        ))}
      </nav>
    </>
  );
}

export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose
}: {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const location = useLocation();
  const previousPath = React.useRef(location.pathname);

  React.useEffect(() => {
    if (location.pathname === previousPath.current) return;
    previousPath.current = location.pathname;
    onMobileClose();
  }, [location.pathname, onMobileClose]);

  return (
    <>
      <aside
        data-collapsed={collapsed}
        className={cn(
          'bg-sidebar text-sidebar-foreground border-border hidden h-full shrink-0 flex-col border-r transition-[width] duration-200 motion-reduce:transition-none md:flex',
          collapsed ? 'w-14' : 'w-60'
        )}
      >
        <SidebarPanel collapsed={collapsed} onToggle={onToggle} />
      </aside>
      <div
        aria-hidden="true"
        onClick={onMobileClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 md:hidden motion-reduce:transition-none',
          mobileOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
      />
      <aside
        aria-label="Navigation"
        className={cn(
          'bg-sidebar text-sidebar-foreground border-border fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r shadow-xl transition-transform duration-200 motion-reduce:transition-none md:hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarPanel collapsed={false} onToggle={onToggle} onClose={onMobileClose} />
      </aside>
    </>
  );
}
